/**
 * **词语表导入预览：这次要导入的词**（词卡网格的数据，2026-10-23）。
 *
 * ## 为什么要有它（用户原话）
 *
 * 词语表预览**从来不列词** —— 字卡网格被 `!isWordList` 挡着，家长导入词语表时
 * 只看到一串数字与「重复明细」，**看起来一片空白**，没东西可核对。用户同意
 * 在这里补一张**只展示**的词卡网格：
 *
 *   · 拼音在上、词在下（与字卡同一种排法，见 `HanziCard`）；
 *   · 拼音是**多音节**的整词拼音（`kàn jiàn`），不是单个字的音；
 *   · 一行 = 一块：`第 N 行（X 个词）`，与字卡网格一样**按粘贴的行分块**；
 *   · 词再多也**一次全显示**（225 个也一样），不分页、不做「再显示 100 个」。
 *
 * ## 数据从哪来（**不另写一套解析器**）
 *
 * 分两路，都是现成的、权威的那一份：
 *
 *   1. **预览报告里的词行**（`report.created / updated / skipped` 里
 *      `kind === 'word'` 的条目）—— 「这次要导入什么」以它为准：后端已经去过重、
 *      跳过了解析失败的行，界面不自己再数一遍；
 *   2. **粘贴框里的原文**用**后端同一个解析器** `parseDictationImport`
 *      （`@fht/shared`，与 `lib/importCounter.ts` 同一路数）跑一遍 —— 只为两件事：
 *      **行内顺序**（报告的每一行没有「这是本行第几个词」这个字段）与
 *      **整词拼音**（报告的行里不带拼音）。
 *
 * 两路按 `行号 + 词` 对齐：报告里的每个词取解析结果里同一个词的拼音与序号；
 * 对不上时退回 `joinWordPinyin()`（后端存词时算自动拼音用的**同一个函数**）。
 * 报告里一条词都没有（老后端 / 只有文本）时，直接用解析结果。
 *
 * ⛔ 这里**只读**：不发请求、不写库、不改 `report`（`groupByLine()` 也不改入参）。
 * ⛔ 这里**不渲染**：卡片长什么样在 `WordCardGrid.tsx`；本文件是纯逻辑、好测。
 */
import { joinWordPinyin, parseDictationImport } from '@fht/shared';
import type { ImportReportView, ImportRowView } from '../../lib/api/dictation';
import { groupByLine, type LineGroup } from './importPreviewLineGroups';

/**
 * 网格里的一张词卡。
 *
 * ⚠️ `indexInLine` = **这一行的第几个词**（1 开始），与字表那边 `sort_order`
 *    同口径：组内按它升序，于是卡片顺序 = 粘贴顺序。
 */
export interface WordCardItem {
  /** 原文行号（1 开始）。 */
  line: number;
  /** 这一行的第几个词（1 开始）。 */
  indexInLine: number;
  /** 词本身。 */
  wordText: string;
  /** 整词拼音（多音节，空格分隔）；空串 = 内置字表里查不到。 */
  pinyin: string;
}

/** 一个「行分组」（与字表那边共用 `LineGroup`，只是条目换成了词卡）。 */
export type WordCardLineGroup = LineGroup<WordCardItem>;

/** 行标题里的量词（词语表：`第 3 行（7 个词）`）。 */
export const WORD_LINE_UNIT = '个词';

/**
 * 网格顶部那句（**逐字**，用户点名的文案）：
 *
 *     这次要导入的词（共 225 个、25 行，按你粘贴的行分组）
 *
 * ⚠️ 与字表那句（`这次要导入的字（共 N 个字、M 行，按你粘贴的行分组）`）分开写：
 *    这里说「N 个」而不是「N 个词」，是用户给的原文，别顺手"统一"成另一种。
 */
export function describeWordPreviewTitle(wordCount: number, lineCount: number): string {
  return `这次要导入的词（共 ${wordCount} 个、${lineCount} 行，按你粘贴的行分组）`;
}

/** 报告里所有**词**行（新增 / 更新 / 已存在、跳过都在内；顺序按报告给的）。 */
export function wordRowsOf(report: ImportReportView | null): ImportRowView[] {
  if (report === null) return [];
  const rows: ImportRowView[] = [];
  for (const list of [report.created, report.updated, report.skipped]) {
    if (list === null) continue;
    for (const row of list) {
      if (row.kind !== 'word') continue;
      if (row.text.trim() === '') continue;
      rows.push(row);
    }
  }
  return rows;
}

/**
 * 用**后端同一个解析器**把原文拆成词卡（行号 / 行内序号 / 整词拼音）。
 *
 * ⚠️ `defaultListType` 固定 `cibiao`、`defaultTextbook` 与发预览请求时那份一致：
 *    解析结果会受它们影响，不一致就会出现"卡片上的行号与预览对不上"。
 * ⚠️ 解析器抛错时**返回空数组**（面板不崩）：预览报告那一份仍然是权威口径。
 */
export function parseWordCardsFromText(text: string, defaultTextbook = ''): WordCardItem[] {
  if (text.trim() === '') return [];
  let parsed: ReturnType<typeof parseDictationImport>;
  try {
    parsed = parseDictationImport(text, {
      defaultListType: 'cibiao',
      ...(defaultTextbook !== '' ? { defaultTextbook } : {}),
    });
  } catch {
    return [];
  }

  /** 每一行已经排到第几个词（解析器给的 `line` 有重复 ⇒ 按出现顺序累加）。 */
  const counters = new Map<number, number>();
  const cards: WordCardItem[] = [];
  for (const row of parsed.rows) {
    const wordText = (row.wordText ?? '').trim();
    if (wordText === '') continue;
    const indexInLine = (counters.get(row.line) ?? 0) + 1;
    counters.set(row.line, indexInLine);
    const manual = (row.pinyin ?? '').trim();
    cards.push({
      line: row.line,
      indexInLine,
      wordText,
      pinyin: manual !== '' ? manual : joinWordPinyin(wordText),
    });
  }
  return cards;
}

/** 对齐用的键：同一行、同一个词。 */
function cardKey(line: number, wordText: string): string {
  return `${line}|${wordText}`;
}

/**
 * 把「报告里的词行」与「原文解析结果」合成词卡列表（**按行号升序、组内按粘贴顺序**）。
 *
 * 报告为空时完全用解析结果（老后端 / 只有文本的预览）；报告有词时以报告为准，
 * 解析结果只提供 `indexInLine` 与 `pinyin` 两样补充信息。
 */
export function buildWordCards(input: {
  report: ImportReportView | null;
  text: string;
  defaultTextbook?: string;
}): WordCardItem[] {
  const parsed = parseWordCardsFromText(input.text, input.defaultTextbook ?? '');
  const fromReport = wordRowsOf(input.report);

  /** 没有报告词行（或报告根本没给）⇒ 解析结果就是全部。 */
  if (fromReport.length === 0) return parsed;

  /** 解析结果按 `行|词` 排队：同一行里同一个词出现两次时按出现顺序各取一份。 */
  const parsedByKey = new Map<string, WordCardItem[]>();
  for (const card of parsed) {
    const key = cardKey(card.line, card.wordText);
    const bucket = parsedByKey.get(key);
    if (bucket === undefined) parsedByKey.set(key, [card]);
    else bucket.push(card);
  }

  /** 先按行收集；行内顺序：解析结果给的序号优先，没有就排在后面（保持报告顺序）。 */
  const byLine = new Map<number, { card: WordCardItem; sortKey: number }[]>();
  for (const row of fromReport) {
    const wordText = row.text.trim();
    const match = parsedByKey.get(cardKey(row.line, wordText))?.shift();
    const card: WordCardItem = {
      line: row.line,
      indexInLine: 0,
      wordText,
      pinyin: match !== undefined && match.pinyin !== '' ? match.pinyin : joinWordPinyin(wordText),
    };
    const entry = { card, sortKey: match?.indexInLine ?? Number.MAX_SAFE_INTEGER };
    const bucket = byLine.get(row.line);
    if (bucket === undefined) byLine.set(row.line, [entry]);
    else bucket.push(entry);
  }

  const cards: WordCardItem[] = [];
  for (const [line, entries] of byLine) {
    /**
     * `sortKey` 相同（解析结果没对上）时**稳定排序**保留报告里的先后 ——
     * `Array.prototype.sort` 在 V8 里是稳定的，与 `groupByLine` 里那处同一条依据。
     */
    entries.sort((left, right) => left.sortKey - right.sortKey);
    entries.forEach((entry, index) => {
      cards.push({ ...entry.card, line, indexInLine: index + 1 });
    });
  }
  return cards;
}

/**
 * 把词卡分成「行」：一行 = 一块，行号升序、组内按 `indexInLine`。
 *
 * ⚠️ 复用**字表那同一份** `groupByLine()`（`单位 = 个词`）：
 *    分组口径（一行一块、排序、`listType|line` 键）两边一模一样，不许另抄一份。
 */
export function groupWordCardsByLine(
  cards: readonly WordCardItem[],
): WordCardLineGroup[] {
  return groupByLine(cards, { unit: WORD_LINE_UNIT });
}

/** 词卡总数（标题里的 N）。 */
export function countWordCards(lines: readonly WordCardLineGroup[]): number {
  let total = 0;
  for (const line of lines) total += line.positions.length;
  return total;
}

/**
 * 一步到位：报告 + 原文 → 分好行的词卡（面板与测试都只调这一个入口）。
 */
export function buildWordCardLines(input: {
  report: ImportReportView | null;
  text: string;
  defaultTextbook?: string;
}): WordCardLineGroup[] {
  return groupWordCardsByLine(buildWordCards(input));
}
